import axios, { InternalAxiosRequestConfig } from 'axios';
import { IST } from '../lib';
import { scopeUserFor } from '../scopeStore';
import {
  Lead,
  LeadListResponse,
  LeadFilters,
  User,
  AdvisorStage,
  LeadAssignment,
  SourceMapping,
  Task,
  TaskListResponse,
  LeadActivity,
  SiteVisitBookRequest,
  SiteVisitBookResponse,
  LeadActivityResponse,
  LeadEngagement,
  Understanding,
  LeadNote,
  TagsSummary,
  CallWindowsResponse,
  Activity,
  SuggestResponse,
  MyLeadsSummary,
  TodayStats,
  FollowupDot,
  ActivityDot,
  ScopeResponse,
  Team,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_CORE_API_URL || 'http://localhost:8000';
const API_KEY = process.env.REACT_APP_CORE_API_KEY || '';
// nurture-service owns operator-facing action lifecycle (complete/reschedule/cancel).
// Mirrors nurture-app-android's BuildConfig (on the AWS box behind Caddy).
const NURTURE_API_URL = 'https://nurture-service.bhuvanafortunegreens.com';

// Attach the logged-in operator's id so core/nurture can audit + gate by user,
// and the scope pick (whose book they are viewing) as X-Scope-User — core
// bounds every read by it and refuses a pick outside the caller's visible users.
const attachUserId = (config: InternalAxiosRequestConfig) => {
  const storedUser = localStorage.getItem('touchpoint_portal_user');
  if (storedUser) {
    const user = JSON.parse(storedUser);
    if (user.id) {
      (config.headers as any)['X-User-Id'] = user.id;
      const picked = scopeUserFor(user.id);
      if (picked) (config.headers as any)['X-Scope-User'] = picked;
    }
  }
  return config;
};

export const currentUserId = (): string | undefined => {
  try {
    const raw = localStorage.getItem('touchpoint_portal_user');
    return raw ? JSON.parse(raw).id : undefined;
  } catch {
    return undefined;
  }
};

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${API_KEY}`
  }
});
apiClient.interceptors.request.use(attachUserId);

const nurtureClient = axios.create({
  baseURL: NURTURE_API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${API_KEY}`
  }
});
nurtureClient.interceptors.request.use(attachUserId);

// Auth API. Every sign-in names this portal's surface — core refuses a user
// whose `surfaces` allowlist does not carry it (MAG-174). Nurture web and the
// Nurture Android app are one product and share `nurture`.
const SURFACE = 'nurture';

export const authApi = {
  verifyUser: async (email: string, name?: string, pictureUrl?: string, googleId?: string): Promise<User> => {
    const response = await apiClient.post('/api/v1/auth/verify-user', {
      email,
      name,
      picture_url: pictureUrl,
      google_id: googleId,
      surface: SURFACE,
    });
    return response.data;
  },

  loginWithPassword: async (email: string, password: string): Promise<User> => {
    const response = await apiClient.post('/api/v1/auth/login', { email, password, surface: SURFACE });
    return response.data;
  },
};

// Lead API
export const leadApi = {
  list: async (page: number = 1, limit: number = 50, filters?: LeadFilters, sortBy?: string, sortOrder?: string): Promise<LeadListResponse> => {
    const skip = (page - 1) * limit;
    const params: Record<string, any> = { skip, limit };

    if (filters?.search) params.search = filters.search;
    if (filters?.source) params.source = filters.source;
    if (filters?.location) params.location = filters.location;
    if (filters?.stage) params.stage = filters.stage;
    if (filters?.is_active !== undefined) params.is_active = filters.is_active;
    if (filters?.tag) params.tag = filters.tag;
    if (filters?.has_tags !== undefined) params.has_tags = filters.has_tags;
    if (sortBy) params.sort_by = sortBy;
    if (sortOrder) params.sort_order = sortOrder;

    const response = await apiClient.get('/api/v1/leads/', { params });
    return response.data;
  },

  get: async (id: string): Promise<Lead> => {
    const response = await apiClient.get(`/api/v1/leads/${id}`);
    return response.data;
  },

  // Live tag distribution (tags dashboard).
  tagsSummary: async (includeDoNotContact: boolean = false): Promise<TagsSummary> => {
    const response = await apiClient.get('/api/v1/leads/tags/summary', {
      params: includeDoNotContact ? { include_do_not_contact: true } : {},
    });
    return response.data;
  },

  update: async (id: string, data: Partial<Lead>): Promise<Lead> => {
    const response = await apiClient.put(`/api/v1/leads/${id}`, data);
    return response.data;
  },

  // Merge tags onto the lead (dimension semantics; manual: tags survive).
  addTags: async (id: string, tags: string[]): Promise<void> => {
    await apiClient.post(`/api/v1/leads/${id}/tags`, { add: tags.map(tag => ({ tag })) });
  },

  delete: async (id: string): Promise<{ deleted: Record<string, number> }> => {
    const response = await apiClient.delete(`/api/v1/leads/${id}`);
    return response.data;
  },

  // The lead's timeline — the same feed the Nurture app reads.
  getActivity: async (id: string): Promise<LeadActivityResponse> => {
    const response = await apiClient.get(`/api/v1/leads/${id}/activities`);
    return response.data;
  },

  getUnderstanding: async (id: string): Promise<Understanding> => {
    const response = await apiClient.get(`/api/v1/leads/${id}/understanding`);
    return response.data;
  },

  // Lifetime tracked-link engagement (album/watch/form links).
  getEngagement: async (id: string): Promise<LeadEngagement> => {
    const response = await apiClient.get(`/api/v1/leads/${id}/engagement`);
    return response.data;
  },

  addNote: async (id: string, content: string): Promise<LeadNote> => {
    const response = await apiClient.post(`/api/v1/leads/${id}/notes`, { content });
    return response.data;
  },

  // Voice note — one shot: note activity + audio + transcription enqueue.
  // `client_ref` is a per-take UUID so a retried upload lands once.
  addVoiceNote: async (id: string, file: Blob, clientRef: string, content?: string): Promise<Activity> => {
    const form = new FormData();
    form.append('file', file, 'voice-note.webm');
    form.append('client_ref', clientRef);
    if (content) form.append('content', content);
    const response = await apiClient.post(`/api/v1/leads/${id}/notes/voice`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // The merge chokepoint for tags: add (optionally replacing the dimension) or
  // remove exact tags. Returns the lead's post-merge tag list.
  mergeTags: async (id: string, change: { add?: { tag: string; replace_dimension?: boolean }[]; remove?: string[] }): Promise<{ tags: string[] }> => {
    const response = await apiClient.post(`/api/v1/leads/${id}/tags`, {
      add: change.add ?? [], remove: change.remove ?? [], mode: 'merge',
    });
    return response.data;
  },

  // On-demand AI lead review → one proposed task, or a skip reason. 2-6s.
  suggest: async (id: string): Promise<SuggestResponse> => {
    const response = await apiClient.post(`/api/v1/leads/${id}/suggest`);
    return response.data;
  },
};

// Tasks — the work queue (core). A lead's upcoming site visit is its open
// `site_visit` task.
export const taskApi = {
  // Queue-wide list. Core embeds the lead snapshot (joinedload) and orders by
  // due_at asc; it returns the full set, so there is no limit param.
  list: async (params: { status?: string; type?: string; assignee?: string; due_before?: string }): Promise<TaskListResponse> => {
    const response = await apiClient.get('/api/v1/tasks', { params });
    return response.data;
  },

  listForLead: async (leadId: string, status?: string): Promise<TaskListResponse> => {
    const response = await apiClient.get(`/api/v1/leads/${leadId}/tasks`, {
      params: status ? { status } : {},
    });
    return response.data;
  },

  // Triage of an AI-suggested (`proposed`) task. Approval is a lifecycle
  // transition on the SAME row — proposed → open, or → cancelled with
  // closed_reason='rejected'. There is no parallel proposal store (§3.25).
  approve: async (taskId: string, dueAt?: string): Promise<Task> => {
    const response = await apiClient.post(`/api/v1/tasks/${taskId}/approve`,
      dueAt ? { due_at: dueAt } : {});
    return response.data;
  },

  reject: async (taskId: string, reason?: string): Promise<Task> => {
    const response = await apiClient.post(`/api/v1/tasks/${taskId}/reject`, { reason });
    return response.data;
  },

  // A to-do: a core task that never fires, so it skips nurture.
  openTodo: async (leadId: string, dueAt: string, title: string): Promise<Task> => {
    const response = await apiClient.post('/api/v1/tasks', {
      lead_id: leadId, type: 'other', due_at: dueAt,
      assignee_user_id: currentUserId(), payload: { title },
    });
    return response.data;
  },
};

// The call-cadence policy's daily calling windows — what makes a task placed at
// a window's start not late a minute later. Core reads them from the policy
// file it mounts, so the ranges live in one place for every surface.
export const schedulingApi = {
  windows: async (): Promise<CallWindowsResponse> => {
    const response = await apiClient.get('/api/v1/scheduling/windows');
    return response.data;
  },
};

// Cross-lead activity list — the immutable timeline, read here for CONDUCTED
// visits (`kind=visit`). Scheduled visits are open tasks; a visit that happened
// is an activity. The two together are the visit board.
export const activityApi = {
  // Core's generic append verb. Idempotent on (source, source_ref), so a retry
  // lands once; refs are namespaced `operator:<subtype>:<id>`.
  append: async (req: {
    lead_id: string;
    kind: 'call' | 'message' | 'note' | 'visit';
    channel: 'phone' | 'whatsapp' | 'internal' | 'system';
    direction: 'inbound' | 'outbound' | 'none';
    source_ref: string;
    summary?: string;
    task_id?: string;
    payload: Record<string, any>;
  }): Promise<Activity> => {
    const response = await apiClient.post('/api/v1/activities', {
      ...req,
      occurred_at: new Date().toISOString(),
      actor_user_id: currentUserId(),
      source: 'operator',
    });
    return response.data;
  },

  list: async (params: {
    kind?: string;
    /** 'me' = the signed-in operator's own activities */
    actor_user_id?: string;
    occurred_from?: string;
    occurred_to?: string;
    limit?: number;
  }): Promise<{ activity: LeadActivity[]; total: number }> => {
    const response = await apiClient.get('/api/v1/activities', { params });
    return response.data;
  },
};

// Site-visit booking — THE single path shared by every surface. Booking sends
// the lead the WhatsApp confirmation and books the advisor's T-2 reminder call;
// calling again with a new date/slot reschedules the open visit.
export const siteVisitApi = {
  book: async (leadId: string, req: SiteVisitBookRequest): Promise<SiteVisitBookResponse> => {
    const response = await apiClient.post(`/api/v1/leads/${leadId}/site-visit`, req);
    return response.data;
  },
};

// Operator task verbs (nurture, task ids; the URL path keeps nurture's
// `/actions/{id}` spelling). Backend hooks handle per-type side effects, e.g.
// visit reminder cleanup. Reads live on taskApi above.
export const actionApi = {
  complete: async (taskId: string, notes?: string, resultActivityId?: string): Promise<void> => {
    await nurtureClient.post(`/api/v1/actions/${taskId}/complete`, {
      notes: notes || undefined,
      result_activity_id: resultActivityId,
      actor_user_id: currentUserId(),
    });
  },

  // Call / WhatsApp work that fires — nurture arms the Cloud Task.
  schedule: async (req: { lead_id: string; type: 'call' | 'whatsapp'; scheduled_at: string; title?: string; data?: Record<string, any> }): Promise<Task> => {
    const me = currentUserId();
    const response = await nurtureClient.post('/api/v1/actions/schedule', {
      ...req, data: req.data ?? {}, assigned_to: me, actor_user_id: me,
    });
    return response.data;
  },

  // A proposed task into the live queue at the operator's (possibly edited)
  // time — the approve twin of schedule.
  approve: async (taskId: string, dueAt: string, data: Record<string, any>): Promise<Task> => {
    const response = await nurtureClient.post(`/api/v1/actions/${taskId}/approve`, {
      due_at: dueAt, data, actor_user_id: currentUserId(),
    });
    return response.data;
  },

  reschedule: async (taskId: string, newTime: string, reason?: string): Promise<void> => {
    await nurtureClient.post(`/api/v1/actions/${taskId}/reschedule`, {
      new_time: newTime, reason: reason || undefined, actor_user_id: currentUserId(),
    });
  },

  cancel: async (taskId: string, reason: string): Promise<void> => {
    await nurtureClient.post(`/api/v1/actions/${taskId}/cancel`, {
      reason,
      actor_user_id: currentUserId(),
    });
  },
};

// Users API
export const usersApi = {
  list: async (isActive?: boolean): Promise<{ total: number; data: User[] }> => {
    const params: Record<string, any> = {};
    if (isActive !== undefined) params.is_active = isActive;
    const response = await apiClient.get('/api/v1/users/', { params });
    return response.data;
  },

  create: async (data: { email: string; name: string; phone?: string; role?: string; surfaces?: string[]; team_id?: string | null; password?: string }): Promise<User> => {
    const response = await apiClient.post('/api/v1/users/', data);
    return response.data;
  },

  // core replaces the WHOLE metadata column — callers merge before sending.
  update: async (id: string, data: { name?: string; phone?: string; role?: string; surfaces?: string[]; team_id?: string | null; is_active?: boolean; password?: string; metadata?: Record<string, any> }): Promise<User> => {
    const response = await apiClient.put(`/api/v1/users/${id}`, data);
    return response.data;
  },

  deactivate: async (id: string): Promise<User> => {
    const response = await apiClient.delete(`/api/v1/users/${id}`);
    return response.data;
  },
};

// Sell.do's teams as core mirrors them (MAG-168); written only by the import.
export const teamsApi = {
  list: async (): Promise<Team[]> => {
    const response = await apiClient.get('/api/v1/teams');
    return response.data;
  },
};

// The signed-in operator's reads — the same `/users/me/*` the Nurture app's
// Today, Leads and Dashboard tabs call. Core bounds them by the scope
// (X-User-Id + the X-Scope-User pick the interceptor attaches).
export const meApi = {
  // Who the caller may view as — feeds the sidebar scope control.
  scope: async (): Promise<ScopeResponse> => {
    const response = await apiClient.get('/api/v1/users/me/scope');
    return response.data;
  },

  leadsSummary: async (): Promise<MyLeadsSummary> => {
    const response = await apiClient.get('/api/v1/users/me/leads/summary');
    return response.data;
  },

  todayStats: async (): Promise<TodayStats> => {
    const response = await apiClient.get('/api/v1/users/me/stats/today', { params: { tz: IST } });
    return response.data;
  },

  // Free [from, to) ranges; all day/week/month bucketing is client-side.
  followups: async (from: string, to: string): Promise<{ total: number; tasks: FollowupDot[] }> => {
    const response = await apiClient.get('/api/v1/users/me/followups/timeline', { params: { from, to } });
    return response.data;
  },

  activity: async (from: string, to: string): Promise<{ total: number; activities: ActivityDot[] }> => {
    const response = await apiClient.get('/api/v1/users/me/activity/timeline', { params: { from, to } });
    return response.data;
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    await apiClient.post('/api/v1/users/me/change-password', {
      current_password: currentPassword, new_password: newPassword,
    });
  },
};

// Assignment API
export const assignmentApi = {
  getForLead: async (leadId: string): Promise<{ lead_id: string; assignments: LeadAssignment[] }> => {
    const response = await apiClient.get(`/api/v1/leads/${leadId}/assignments`);
    return response.data;
  },

  setStage: async (leadId: string, stage: AdvisorStage, userId: string | null): Promise<LeadAssignment> => {
    const response = await apiClient.put(
      `/api/v1/leads/${leadId}/assignments/${stage}`,
      { user_id: userId }
    );
    return response.data;
  },

  listSourceMappings: async (): Promise<SourceMapping[]> => {
    const response = await apiClient.get('/api/v1/source-mappings/');
    return response.data;
  },

  createSourceMapping: async (source: string, userId: string): Promise<SourceMapping> => {
    const response = await apiClient.post('/api/v1/source-mappings/', { source, user_id: userId });
    return response.data;
  },

  deleteSourceMapping: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/source-mappings/${id}`);
  },
};


export default apiClient;
